'use client';

import {useState} from 'react';
import Link from 'next/link';
import {useAuth} from '@/contexts/AuthContext';
import {CheckCircle2, CircleHelp, CreditCard, KeyRound, Loader2, MailCheck, RefreshCw, ShieldCheck} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

export default function AcessoPosCompraPage() {
  const {user, company, loading, refreshProfile, signOut} = useAuth();
  const [checking,setChecking] = useState(false);
  const [checkDone,setCheckDone] = useState(false);
  const [checkError,setCheckError] = useState('');
  const active = company?.plan === 'pro' && company?.subscription_status === 'active' &&
    (!company?.pro_expires_at || new Date(company.pro_expires_at).getTime() > Date.now());

  async function checkPurchase() {
    setChecking(true);
    setCheckError('');
    setCheckDone(false);
    try {
      await refreshProfile();
      setCheckDone(true);
    } catch {
      setCheckError('Não foi possível atualizar o acesso. Verifique sua conexão e tente novamente.');
    } finally {
      setChecking(false);
    }
  }

  return (
    <main className="min-h-screen bg-[#0d0d0f] px-4 py-10 text-white">
      <div className="mx-auto max-w-xl">
        <Link href="/login" className="mb-7 flex items-center justify-center gap-2" aria-label="FluxoSim, login">
          <AppLogo size={42}/>
          <span className="text-2xl font-extrabold">Fluxo<span className="text-orange-500">Sim</span></span>
        </Link>
        <div className="rounded-3xl border border-zinc-700 bg-zinc-900 p-6 shadow-2xl sm:p-9">
          <p className="text-sm font-bold uppercase tracking-widest text-orange-400">Boas-vindas ao FluxoSim</p>
          <h1 className="mt-2 text-3xl font-extrabold">Comprou o FluxoSim PRO? Acesse aqui.</h1>
          <p className="mt-3 text-sm leading-6 text-zinc-300">
            Você não precisa comprar outra vez. Entre com o <strong>mesmo e-mail informado na Eduzz</strong>.
            A senha é criada no FluxoSim, não na Eduzz.
          </p>

          {loading ? (
            <p className="mt-6 flex items-center gap-2 text-sm text-zinc-300"><Loader2 size={18} className="animate-spin"/> Conferindo sua sessão...</p>
          ) : active ? (
            <section className="mt-7 rounded-2xl border border-green-700/50 bg-green-900/20 p-5">
              <div className="flex items-start gap-3"><CheckCircle2 size={22} className="shrink-0 text-green-400"/>
                <div><h2 className="font-bold text-green-300">Seu PRO está liberado!</h2>
                  <p className="mt-1 text-xs text-zinc-300">Conta: {user?.email}</p>
                  {company?.pro_expires_at && <p className="mt-1 text-xs text-zinc-300">
                    Validade: {new Date(company.pro_expires_at).toLocaleDateString('pt-BR')}
                  </p>}</div>
              </div>
              <Link href="/" className="mt-5 block rounded-xl bg-green-600 px-4 py-3 text-center font-bold text-white">
                Entrar no FluxoSim
              </Link>
            </section>
          ) : !user ? (
            <section className="mt-7 grid gap-3">
              <Link href="/login?next=%2Facesso"
                className="flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3.5 font-bold text-black">
                <KeyRound size={18}/> Já tenho conta — Entrar
              </Link>
              <Link href="/cadastro"
                className="flex items-center justify-center gap-2 rounded-xl border border-zinc-500 px-4 py-3.5 font-bold text-white">
                <MailCheck size={18}/> Primeiro acesso — Criar minha senha
              </Link>
              <p className="text-center text-xs text-zinc-400">
                Se comprou antes de se cadastrar, crie a conta usando exatamente o e-mail da compra.
              </p>
            </section>
          ) : (
            <section className="mt-7 space-y-3 rounded-2xl border border-orange-700/50 bg-orange-900/10 p-5">
              <h2 className="flex items-center gap-2 font-semibold text-orange-200"><CreditCard size={20}/> Verificar minha compra</h2>
              <p className="text-sm text-zinc-300">Você está conectado como <strong>{user.email}</strong>.</p>
              <p className="text-xs leading-5 text-zinc-300">
                Sua conta ainda não mostra o PRO. Se acabou de pagar, confira o e-mail no comprovante da Eduzz.
                A integração usa o e-mail confirmado do comprador.
              </p>
              <button type="button" onClick={()=>void checkPurchase()} disabled={checking}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-orange-500 px-4 py-3 font-bold text-black disabled:opacity-50">
                {checking?<Loader2 size={18} className="animate-spin"/>:<RefreshCw size={18}/>}
                {checking?'Conferindo...':'Já paguei — Verificar novamente'}
              </button>
              {checkError && <p role="alert" className="text-xs text-red-300">{checkError}</p>}
              {checkDone && !active &&
                <p role="status" className="text-xs text-amber-200">
                  Caso o pagamento já esteja aprovado, não pague outra vez. Confirme que a compra foi feita com
                  este mesmo e-mail. Se não foi, contate o suporte para verificar a titularidade.
                </p>}
              <button type="button" onClick={()=>void signOut()}
                className="w-full rounded-xl border border-zinc-600 px-4 py-2.5 text-sm font-semibold text-zinc-100">
                Entrei com outro e-mail — Trocar conta
              </button>
            </section>
          )}

          <div className="mt-8 border-t border-zinc-700 pt-6">
            <h2 className="text-sm font-bold text-white">Como funciona o acesso</h2>
            <ol className="mt-3 space-y-3 text-sm leading-5 text-zinc-300">
              <li><strong className="text-orange-400">1.</strong> Use o mesmo e-mail informado na compra.</li>
              <li><strong className="text-orange-400">2.</strong> Se for sua primeira vez, crie uma conta e confirme o e-mail quando solicitado.</li>
              <li><strong className="text-orange-400">3.</strong> Entre e clique em &ldquo;Já paguei — Verificar novamente&rdquo; se necessário.</li>
            </ol>
            <p className="mt-5 flex items-start gap-2 text-xs leading-5 text-zinc-400">
              <ShieldCheck size={17} className="mt-0.5 shrink-0 text-orange-400"/>
              Por segurança, a compra feita com um e-mail diferente não é transferida sem verificação.
            </p>
            <p className="mt-4 flex items-start gap-2 text-xs text-zinc-300">
              <CircleHelp size={17} className="shrink-0 text-orange-400"/>
              Precisa de ajuda?
              <a className="break-all font-semibold text-orange-300 underline"
                href="mailto:frontusinagem@gmail.com?subject=Ajuda%20com%20acesso%20FluxoSim%20PRO">
                frontusinagem@gmail.com
              </a>
            </p>
          </div>
        </div>
      </div>
    </main>
  );
}
